import { Component, ElementRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { LucidePin, LucidePlus, LucideStar, LucideX } from '@lucide/angular';
import { describeGenericApiError } from '../../core/http/api-error.util';
import { MeService } from '../settings/me.service';
import { Task } from '../tasks/task.model';
import { TaskService } from '../tasks/task.service';
import { FocusService } from './focus.service';
import { FocusGroup, FocusViewResponse } from './focus.model';

@Component({
  selector: 'app-focus',
  standalone: true,
  imports: [CommonModule, RouterLink, LucidePin, LucidePlus, LucideStar, LucideX],
  templateUrl: './focus.component.html',
  styleUrls: ['./focus.component.scss']
})
export class FocusComponent implements OnInit {
  @ViewChild('focusGroups') private focusGroupsRef?: ElementRef<HTMLElement>;

  private readonly focusService = inject(FocusService);
  private readonly taskService = inject(TaskService);
  private readonly meService = inject(MeService);

  readonly view = signal<FocusViewResponse | null>(null);
  readonly focusDailyTarget = signal<number | null>(null);
  readonly priorityDailyTarget = signal<number | null>(null);
  readonly initialLoading = signal(false);
  readonly refreshing = signal(false);
  readonly error = signal<string | null>(null);
  readonly submittingIds = signal<Record<string, boolean>>({});
  readonly activeAddGroupId = signal<string | null>(null);
  readonly addingTask = signal(false);
  newTaskTitle = '';

  readonly todaysPriorityIds = computed(() =>
    new Set((this.view()?.todaysPriorities ?? []).map((task) => task.id))
  );
  readonly todaysPrioritiesActive = computed(() =>
    this.view()?.todaysPriorities.filter((task) => task.completedAt === null) ?? []
  );
  readonly todaysPrioritiesCompleted = computed(() =>
    this.view()?.todaysPriorities.filter((task) => task.completedAt !== null) ?? []
  );
  readonly todaysFocusActive = computed(() =>
    this.view()?.todaysFocus.filter((task) => (
      task.completedAt === null && !this.todaysPriorityIds().has(task.id)
    )) ?? []
  );
  readonly todaysFocusCompleted = computed(() =>
    this.view()?.todaysFocus.filter((task) => (
      task.completedAt !== null && !this.todaysPriorityIds().has(task.id)
    )) ?? []
  );
  readonly overFocusTarget = computed(() => {
    const target = this.focusDailyTarget();
    if (target === null) {
      return false;
    }
    return this.todaysFocusActive().length > target;
  });
  readonly overPriorityTarget = computed(() => {
    const target = this.priorityDailyTarget();
    if (target === null) {
      return false;
    }
    return this.todaysPrioritiesActive().length > target;
  });

  ngOnInit(): void {
    this.load();
    this.loadTargets();
  }

  load(): void {
    this.initialLoading.set(true);
    this.error.set(null);
    this.focusService.getTodayView().subscribe({
      next: (response) => {
        this.view.set(response);
        this.initialLoading.set(false);
      },
      error: (err: unknown) => {
        this.error.set(describeGenericApiError(err, 'Failed to load focus view'));
        this.initialLoading.set(false);
      }
    });
  }

  focusTask(task: Task): void {
    this.setSubmitting(task.id, true);
    this.error.set(null);
    this.taskService.focus(task.id).subscribe({
      next: () => {
        this.setSubmitting(task.id, false);
        this.silentRefresh();
      },
      error: (err: unknown) => {
        this.setSubmitting(task.id, false);
        this.error.set(describeGenericApiError(err, 'Failed to focus task'));
      }
    });
  }

  unfocusTask(task: Task): void {
    this.setSubmitting(task.id, true);
    this.error.set(null);
    this.taskService.unfocus(task.id).subscribe({
      next: () => {
        this.setSubmitting(task.id, false);
        this.silentRefresh();
      },
      error: (err: unknown) => {
        this.setSubmitting(task.id, false);
        this.error.set(describeGenericApiError(err, 'Failed to remove task from focus'));
      }
    });
  }

  prioritizeTask(task: Task): void {
    this.setSubmitting(task.id, true);
    this.error.set(null);
    this.taskService.prioritize(task.id).subscribe({
      next: () => {
        this.setSubmitting(task.id, false);
        this.silentRefresh();
      },
      error: (err: unknown) => {
        this.setSubmitting(task.id, false);
        this.error.set(describeGenericApiError(err, 'Failed to prioritize task'));
      }
    });
  }

  unprioritizeTask(task: Task): void {
    this.setSubmitting(task.id, true);
    this.error.set(null);
    this.taskService.unprioritize(task.id).subscribe({
      next: () => {
        this.setSubmitting(task.id, false);
        this.silentRefresh();
      },
      error: (err: unknown) => {
        this.setSubmitting(task.id, false);
        this.error.set(describeGenericApiError(err, 'Failed to remove task from priorities'));
      }
    });
  }

  completeTask(task: Task): void {
    this.setSubmitting(task.id, true);
    this.error.set(null);
    this.taskService.complete(task.id).subscribe({
      next: () => {
        this.setSubmitting(task.id, false);
        this.silentRefresh();
      },
      error: (err: unknown) => {
        this.setSubmitting(task.id, false);
        this.error.set(describeGenericApiError(err, 'Failed to complete task'));
      }
    });
  }

  openAddTask(group: FocusGroup): void {
    this.activeAddGroupId.set(group.activityTypeId);
    this.newTaskTitle = '';
  }

  cancelAddTask(): void {
    this.activeAddGroupId.set(null);
    this.newTaskTitle = '';
  }

  addTaskToGroup(group: FocusGroup): void {
    const title = this.newTaskTitle.trim();
    if (!title || this.addingTask()) {
      return;
    }

    this.addingTask.set(true);
    this.error.set(null);
    this.taskService.create({ activityTypeId: group.activityTypeId, title }).subscribe({
      next: () => {
        this.newTaskTitle = '';
        this.activeAddGroupId.set(null);
        this.addingTask.set(false);
        this.silentRefresh();
      },
      error: (err: unknown) => {
        this.error.set(describeGenericApiError(err, 'Failed to add task'));
        this.addingTask.set(false);
      }
    });
  }

  isSubmitting(taskId: string): boolean {
    return !!this.submittingIds()[taskId];
  }

  isTaskFocusedToday(task: Task): boolean {
    return task.focusedDate === this.view()?.dayDate;
  }

  isTaskPrioritizedToday(task: Task): boolean {
    return task.prioritizedDate === this.view()?.dayDate;
  }

  private loadTargets(): void {
    this.meService.getMe().subscribe({
      next: (response) => {
        this.focusDailyTarget.set(response.settings?.focusDailyTarget ?? null);
        this.priorityDailyTarget.set(response.settings?.priorityDailyTarget ?? null);
      },
      error: () => {
        this.focusDailyTarget.set(null);
        this.priorityDailyTarget.set(null);
      }
    });
  }

  private silentRefresh(): void {
    this.refreshing.set(true);
    const anchorEl = this.focusGroupsRef?.nativeElement ?? null;
    const anchorTopBefore = anchorEl?.getBoundingClientRect().top ?? null;

    this.focusService.getTodayView().subscribe({
      next: (response) => {
        this.view.set(response);
        requestAnimationFrame(() => {
          if (anchorEl && anchorTopBefore !== null) {
            const delta = anchorEl.getBoundingClientRect().top - anchorTopBefore;
            if (delta !== 0) {
              window.scrollBy({ top: delta, behavior: 'instant' });
            }
          }
          this.refreshing.set(false);
        });
      },
      error: (err: unknown) => {
        this.error.set(describeGenericApiError(err, 'Failed to load focus view'));
        this.refreshing.set(false);
      }
    });
  }

  private setSubmitting(taskId: string, value: boolean): void {
    this.submittingIds.update((current) => ({ ...current, [taskId]: value }));
  }
}
